import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, MenuItem, Select, FormControl, InputLabel,
    Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, InputAdornment, TablePagination
} from "@mui/material";
import { Add, Search, Edit, Delete, AccountBalanceWallet, FilterList } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

function Advancepayment() {
    const [staffList, setStaffList] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [form, setForm] = useState({
        staffId: "",
        payment: "",
        date: new Date().toLocaleDateString('en-CA'),
        payType: "Cash",
        note: ""
    });

    useEffect(() => {
        loadStaff();
        loadAdvances();
    }, []);

    const loadStaff = async () => {
        const snapshot = await getDocs(collection(db, "staff"));
        setStaffList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const loadAdvances = async () => {
        const q = query(collection(db, "staffadvances"), orderBy("date", "desc"));
        const snapshot = await getDocs(q);
        setAdvances(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const handleOpen = (data = null) => {
        if (data) {
            setEditMode(true);
            setCurrentId(data.id);
            setForm({ ...data });
        } else {
            setEditMode(false);
            setForm({ staffId: "", payment: "", date: new Date().toLocaleDateString('en-CA'), payType: "Cash", note: "" });
        }
        setOpen(true);
    };

    const handleSave = async () => {
        if (!form.staffId || !form.payment) return alert("Please fill required fields");

        const data = { ...form, payment: Number(form.payment), status: form.status || "Pending" };

        if (editMode) {
            await updateDoc(doc(db, "staffadvances", currentId), data);
        } else {
            await addDoc(collection(db, "staffadvances"), data);
        }
        setOpen(false);
        loadAdvances();
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this record?")) {
            await deleteDoc(doc(db, "staffadvances", id));
            loadAdvances();
        }
    };

    const getStaffName = (id) => staffList.find(s => s.id === id)?.name || "Unknown";

    // Search Logic
    const filteredData = advances.filter(item => 
        getStaffName(item.staffId).toLowerCase().includes(search.toLowerCase()) ||
        item.payType.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                
                {/* Header Section */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center" mb={4}>
                    <Box>
                        <Typography variant="h5" fontWeight="800" color="primary.main">Advance Payments</Typography>
                        <Typography variant="caption" color="textSecondary">Manage and track staff salary advances</Typography>
                    </Box>
                    <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        onClick={() => handleOpen()}
                        sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Add Advance
                    </Button>
                </Stack>

                {/* Filter & Search */}
                <Box mb={3}>
                    <TextField
                        placeholder="Search by staff name or payment type..."
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 3, bgcolor: '#fff' }
                        }}
                    />
                </Box>

                {/* Table Layout */}
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Staff Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.date}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{getStaffName(row.staffId)}</Typography>
                                        <Typography variant="caption" color="textSecondary">{row.note}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold" color="error.main">₹{row.payment}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={row.payType} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={row.status} 
                                            size="small" 
                                            color={row.status === "Pending" ? "warning" : "success"}
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleOpen(row)}><Edit fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                        No advance records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 30]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* ADD/EDIT POPUP */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>
                    {editMode ? "Edit Advance" : "New Advance Entry"}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Stack spacing={2.5}>
                        <FormControl fullWidth>
                            <InputLabel>Select Staff</InputLabel>
                            <Select size="small"
                                value={form.staffId}
                                label="Select Staff"
                                onChange={(e) => setForm({...form, staffId: e.target.value})}
                            >
                                {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Amount (₹)" 
                            type="number" 
                            fullWidth size="small" 
                            value={form.payment}
                            onChange={(e) => setForm({...form, payment: e.target.value})}
                        />

                        <TextField 
                            type="date" 
                            label="Date" 
                            fullWidth size="small" 
                            InputLabelProps={{ shrink: true }}
                            value={form.date}
                            onChange={(e) => setForm({...form, date: e.target.value})}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Type</InputLabel>
                            <Select
                                value={form.payType}
                                label="Payment Type"
                                onChange={(e) => setForm({...form, payType: e.target.value})}
                            >
                                <MenuItem value="Cash">Cash</MenuItem>
                                <MenuItem value="Online">Online / UPI</MenuItem>
                                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                <MenuItem value="Sagar Account">Sagar Account</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Reason / Note" 
                            placeholder="e.g. For medical emergency"
                            multiline rows={2} 
                            fullWidth size="small" 
                            value={form.note}
                            onChange={(e) => setForm({...form, note: e.target.value})}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 4 }}>
                        {editMode ? "Update Record" : "Save Entry"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Advancepayment;